"use client";

import { useEffect, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import {
  ArrowLeft,
  ArrowUpRight,
  Braces,
  Building2,
  Cpu,
  FileText,
  Layers3,
  Loader2,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import { buildDocsHref, getProductIntroHref, type DocsProductKey } from "@/config/navigation";

type Step = 1 | 2;
type IntegrationMode = "api" | "saas" | "mcp" | "private";

const productOptions: Array<{
  key: DocsProductKey;
  title: string;
  description: string;
  icon: typeof Sparkles;
}> = [
  {
    key: "smart-ppt",
    title: "智能 PPT",
    description: "长文本生成、大纲转PPT、模板渲染",
    icon: Sparkles,
  },
  {
    key: "smart-resume",
    title: "智能简历",
    description: "简历解析、结构化输出、画像能力",
    icon: FileText,
  },
  {
    key: "smart-contract",
    title: "智能合同",
    description: "合同生成、审批流程、归档场景",
    icon: Building2,
  },
];

const integrationOptions: Array<{
  key: IntegrationMode;
  title: string;
  description: string;
  icon: typeof Braces;
}> = [
  {
    key: "api",
    title: "API 直连",
    description: "自建系统，灵活可控，适合有研发能力的团队",
    icon: Braces,
  },
  {
    key: "saas",
    title: "SaaS 嵌入",
    description: "无需开发，快速上线，适合低代码场景",
    icon: Layers3,
  },
  {
    key: "mcp",
    title: "MCP Server",
    description: "Agent 场景智能调用，适合 AI 工作流集成",
    icon: Cpu,
  },
  {
    key: "private",
    title: "私有化部署",
    description: "数据本地留存，满足安全合规要求",
    icon: ShieldCheck,
  },
];

const docsTargets: Record<DocsProductKey, Record<IntegrationMode, string>> = {
  "smart-ppt": {
    api: "/docs/smart-ppt/api/overview",
    saas: "/docs/smart-ppt/saas/guide",
    mcp: "/docs/smart-ppt/mcp/mcp-server",
    private: "/docs/smart-ppt/guide/choose-method",
  },
  "smart-resume": {
    api: "/docs/smart-resume/api/overview",
    saas: "/docs/smart-resume/guide/choose-method",
    mcp: "/docs/smart-resume/guide/choose-method",
    private: "/docs/smart-resume/guide/choose-method",
  },
  "smart-contract": {
    api: "/docs/smart-contract/guide/api-direct",
    saas: "/docs/smart-contract/guide/api-direct",
    mcp: "/docs/smart-contract/guide/api-direct",
    private: "/docs/smart-contract/guide/api-direct",
  },
};

export function HeroDocsGuideDialog() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>(1);
  const [selectedProduct, setSelectedProduct] = useState<DocsProductKey | null>(null);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    if (!open) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const handleOpen = () => {
    setOpen(true);
    setStep(1);
    setSelectedProduct(null);
    setPendingAction(null);
  };

  const handleClose = () => {
    setOpen(false);
    setStep(1);
    setSelectedProduct(null);
    setPendingAction(null);
  };

  useEffect(() => {
    if (!isPending) {
      setPendingAction(null);
    }
  }, [isPending]);

  const handleProductSelect = (product: DocsProductKey) => {
    if (isPending) {
      return;
    }

    if (product !== "smart-ppt") {
      const href = buildDocsHref(getProductIntroHref(product), product);
      setPendingAction(product);
      setOpen(false);
      startTransition(() => {
        router.push(href);
      });
      return;
    }

    setPendingAction(product);
    startTransition(() => {
      setSelectedProduct(product);
      setStep(2);
    });
  };

  const handleModeSelect = (mode: IntegrationMode) => {
    if (!selectedProduct || isPending) {
      return;
    }

    const href = buildDocsHref(docsTargets[selectedProduct][mode], selectedProduct);
    setPendingAction(mode);
    setOpen(false);
    startTransition(() => {
      router.push(href);
    });
  };

  const handleSkipToDefaultProduct = () => {
    const href = buildDocsHref(getProductIntroHref("smart-ppt"), "smart-ppt");
    setPendingAction("skip");
    setOpen(false);
    startTransition(() => {
      router.push(href);
    });
  };

  const handleOpenSmartPptSelectionGuide = () => {
    const href = buildDocsHref(docsTargets["smart-ppt"].private, "smart-ppt");
    setPendingAction("guide");
    setOpen(false);
    startTransition(() => {
      router.push(href);
    });
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="inline-flex items-center justify-center rounded-full border border-[#d8e3ff] bg-white/84 px-8 py-4 text-sm font-semibold text-[#091223] shadow-[0_14px_30px_rgba(15,23,42,0.05)] transition-all duration-300 hover:-translate-y-1 hover:border-[#bfd0ff] hover:text-[#406EFF] dark:border-white/10 dark:bg-white/[0.04] dark:text-[#f8fafc] dark:hover:text-[#bfd0ff]"
      >
        查看接入文档
      </button>

      {open ? (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-6">
          <button
            type="button"
            aria-label="关闭弹窗"
            className="absolute inset-0 bg-[rgba(6,12,24,0.58)] backdrop-blur-[6px] backdrop-brightness-[0.72]"
            onClick={handleClose}
          />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(64,110,255,0.14),transparent_34%),linear-gradient(180deg,rgba(7,13,25,0.1)_0%,rgba(7,13,25,0.22)_100%)]" />

          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="hero-docs-guide-title"
            className={`relative w-full overflow-hidden rounded-2xl border border-[rgba(255,255,255,0.72)] bg-[linear-gradient(180deg,rgba(255,255,255,0.98)_0%,rgba(248,250,255,0.97)_100%)] text-[#091223] shadow-[0_38px_90px_rgba(3,8,20,0.34),0_14px_34px_rgba(15,23,42,0.14)] transition-all duration-150 ease-out ${
              step === 1 ? "max-w-[980px]" : "max-w-[760px]"
            } animate-[hero-docs-dialog-in_150ms_ease-out]`}
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[linear-gradient(180deg,rgba(255,255,255,0.68)_0%,rgba(255,255,255,0)_100%)]" />
            <button
              type="button"
              aria-label="关闭弹窗"
              onClick={handleClose}
              className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#E5E7EB] bg-white/92 text-[#52627d] shadow-[0_10px_24px_rgba(15,23,42,0.08)] transition-all duration-200 hover:border-[#bfd0ff] hover:text-[#406EFF]"
            >
              <X className="h-4 w-4" />
            </button>
            {step === 1 ? (
              <button
                type="button"
                onClick={handleSkipToDefaultProduct}
                disabled={isPending}
                className="absolute right-4 top-[4.4rem] inline-flex items-center justify-center rounded-full border border-[#E6ECFB] bg-[linear-gradient(180deg,#FFFFFF_0%,#F7FAFF_100%)] px-3 py-1.5 text-xs font-medium text-[#6E7C94] shadow-[0_8px_18px_rgba(15,23,42,0.06)] transition-all duration-200 hover:-translate-y-0.5 hover:border-[#C8D8FF] hover:text-[#406EFF] hover:shadow-[0_12px_24px_rgba(64,110,255,0.12)]"
              >
                {isPending && pendingAction === "skip" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
                跳过
              </button>
            ) : null}

            <div className="border-b border-[#EEF2FF] px-5 py-5 sm:px-7 sm:py-6">
              {step === 2 ? (
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-2 text-sm font-medium text-[#5f6c82] transition-colors duration-200 hover:text-[#406EFF]"
                >
                  <ArrowLeft className="h-4 w-4" />
                  重新选择产品
                </button>
              ) : null}

              <div className={step === 2 ? "mt-4" : ""}>
                <div className="flex flex-wrap items-center gap-3">
                  <h2 id="hero-docs-guide-title" className="text-[1.55rem] font-semibold tracking-[-0.03em] text-[#091223] sm:text-[1.75rem]">
                    {step === 1 ? "你想接入哪个产品？" : "选择你的接入方式"}
                  </h2>
                  {selectedProduct ? (
                    <span className="inline-flex items-center rounded-full bg-[#EEF4FF] px-3 py-1 text-xs font-semibold text-[#406EFF]">
                      {productOptions.find((item) => item.key === selectedProduct)?.title}
                    </span>
                  ) : null}
                </div>
                <p className="mt-2 max-w-[42rem] text-sm leading-7 text-[#60708a] sm:text-[0.95rem]">
                  {step === 1
                    ? "选择产品后，我们将引导你选择最适合的接入方式"
                    : "根据你的团队形态和交付要求，直接进入对应方案文档。"}
                </p>
              </div>
            </div>

            <div className="px-5 py-5 sm:px-7 sm:py-7">
              {step === 1 ? (
                <div className="grid gap-4 md:grid-cols-3">
                  {productOptions.map((product) => (
                    <button
                      key={product.key}
                      type="button"
                      disabled={isPending}
                      aria-busy={isPending && pendingAction === product.key}
                      onClick={() => handleProductSelect(product.key)}
                      className="group flex min-h-[208px] flex-col rounded-xl border border-[#E5E7EB] bg-white p-5 text-left transition-all duration-200 hover:-translate-y-1 hover:border-[#406EFF] hover:shadow-[0_18px_36px_rgba(64,110,255,0.12)] disabled:cursor-wait"
                    >
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#EEF4FF] text-[#406EFF] transition-transform duration-200 group-hover:scale-105">
                        {isPending && pendingAction === product.key ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <product.icon className="h-5 w-5" />
                        )}
                      </div>
                      <div className="mt-6 text-[1.15rem] font-semibold tracking-[-0.02em] text-[#091223]">{product.title}</div>
                      <p className="mt-3 text-sm leading-7 text-[#5f6c82]">{product.description}</p>
                    </button>
                  ))}
                </div>
              ) : (
                <div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {integrationOptions.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        disabled={isPending}
                        aria-busy={isPending && pendingAction === option.key}
                        onClick={() => handleModeSelect(option.key)}
                        className="group flex min-h-[188px] flex-col rounded-xl border border-[#E5E7EB] bg-white p-5 text-left transition-all duration-200 hover:-translate-y-1 hover:border-[#406EFF] hover:shadow-[0_18px_36px_rgba(64,110,255,0.12)] disabled:cursor-wait"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#EEF4FF] text-[#406EFF] transition-transform duration-200 group-hover:scale-105">
                            {isPending && pendingAction === option.key ? (
                              <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                              <option.icon className="h-5 w-5" />
                            )}
                          </div>
                          <ArrowUpRight className="h-4 w-4 text-[#9AA6BA] transition-all duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#406EFF]" />
                        </div>
                        <div className="mt-6 text-[1.08rem] font-semibold tracking-[-0.02em] text-[#091223]">{option.title}</div>
                        <p className="mt-3 text-sm leading-7 text-[#5f6c82]">{option.description}</p>
                      </button>
                    ))}
                  </div>

                  <div className="border-t border-[#EEF2FF] px-1 pt-4 text-center">
                    <button
                      type="button"
                      onClick={handleOpenSmartPptSelectionGuide}
                      disabled={isPending}
                      className="text-xs leading-6 text-[#7A879C] transition-colors duration-200 hover:text-[#406EFF] sm:text-sm"
                    >
                      {isPending && pendingAction === "guide" ? <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" /> : null}
                      不了解这四种接入方式有什么区别？点击查看智能 PPT《选择接入方式》
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <style jsx global>{`
        @keyframes hero-docs-dialog-in {
          0% {
            opacity: 0;
            transform: scale(0.96);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </>
  );
}
